import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActions } from '../../hooks/useStore';
import { useGlobalState } from '../../state/useGlobalState';
import { useImageLibraryStore } from '../../state/createImageLibraryStore';
import { useImageSelectModal } from '../../hooks/useModal';
import { getFormConfig } from './EditModalFormConfig';
import EditModal from './EditModal';
import type { EditContext, AnyItem, ItemWithId, PartyMember, NPC, POI } from '../../types/types';

interface EditModalContainerProps {
  context: EditContext<AnyItem> | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Container component for EditModal (ADR-034).
 *
 * Owns all store access: useActions, useGlobalState.getState, useImageLibraryStore,
 * useImageSelectModal, and useNavigate. Passes all data and callbacks as props to
 * the EditModal presenter.
 */
const EditModalContainer = ({ context, isOpen, onClose }: EditModalContainerProps) => {
  const actions = useActions();
  const navigate = useNavigate();
  const imageLibrary = useImageLibraryStore(state => state.images);
  const { show: showImageSelectModal } = useImageSelectModal();

  const formConfig = useMemo(() => getFormConfig(actions), [actions]);

  // Pass useGlobalState.getState as a stable reference for imperative reads at action time.
  // Config callbacks (onOpen, onSave, createItemFromForm) call getState() inside handlers,
  // not during render, so a function reference is the correct approach.
  const getState = useGlobalState.getState;

  const handleViewOnMap = useCallback((item: ItemWithId | null, itemType: string | null) => {
    if (!item) return;
    const marker = item as PartyMember | NPC | POI;
    // The `showOnMap` property is only relevant for NPCs and POIs, not players.
    const canView = itemType === 'player' || (marker as NPC | POI).showOnMap;
    if (marker.mapId && canView) {
      navigate('/');
      actions.setActiveMap(marker.mapId);
      actions.highlightMarker(marker.id);
      onClose();
    }
  }, [navigate, actions, onClose]);

  const handleHealToFull = useCallback((item: ItemWithId | null) => {
    if (item) {
      actions.healPartyMember(item.id);
      onClose();
    }
  }, [actions, onClose]);

  if (!context || !isOpen) {
    return null;
  }

  return (
    <EditModal
      context={context}
      isOpen={isOpen}
      onClose={onClose}
      formConfig={formConfig}
      getState={getState}
      imageLibrary={imageLibrary}
      showImageSelectModal={showImageSelectModal}
      onViewOnMap={handleViewOnMap}
      onHealToFull={handleHealToFull}
    />
  );
};

export default EditModalContainer;
